/**
 * Chiffrement des entrées de coffre (`docs/ARCHITECTURE.md` §2).
 *
 * Chaque entrée est chiffrée INDIVIDUELLEMENT sous la `vaultKey`, avec un nonce
 * aléatoire frais à chaque écriture. Le serveur ne reçoit qu'un blob opaque.
 *
 * Ce qui est chiffré : nom, identifiant, mot de passe, URL, notes, graine TOTP,
 * champs personnalisés. Ce qui peut rester en clair côté serveur (choix de
 * `vault-model`, hors de ce paquet) : `folderId`, `favorite`, révisions,
 * horodatages — JAMAIS d'URL, d'identifiant ni de mot de passe.
 */
import { SEAL_CONTEXT, SYMMETRIC_KEY_LENGTH, type AeadAlgorithmId } from './constants.js';
import { openFromString, sealToString, type SealedString } from './aead.js';
import { bytesToUtf8, utf8ToBytes, wipe } from './bytes.js';
import { InvalidParameterError } from './errors.js';
import { assertSecretKey, type SecretKey } from './secret.js';

/** Champ personnalisé d'une entrée (nom ET valeur chiffrés). */
export interface EntryCustomField {
  readonly name: string;
  readonly value: string;
  /** Indication d'affichage (masquer la valeur dans l'interface). */
  readonly hidden?: boolean;
  /** Case a cocher (type 2 de Bitwarden, `boolean` du FIDO CXF) : `value` vaut alors `'true'` ou `'false'`. */
  readonly kind?: 'boolean';
}

/**
 * Partie SECRÈTE d'une entrée de coffre. Structure ouverte : `vault-model`
 * précisera le schéma métier, le cœur crypto se contente de la sceller.
 */
export interface EntrySecrets {
  readonly name?: string;
  readonly username?: string;
  readonly password?: string;
  readonly uris?: readonly string[];
  readonly notes?: string;
  /** Graine TOTP (base32) — secret à part entière. */
  readonly totpSecret?: string;
  readonly customFields?: readonly EntryCustomField[];
  readonly [key: string]: unknown;
}

/** Entrée chiffrée, prête à être stockée ou synchronisée. */
export type EncryptedEntry = SealedString;

export interface EncryptEntryInput<T> {
  readonly entry: T;
  readonly vaultKey: SecretKey;
  /**
   * Identifiant d'entrée lié en données associées. FORTEMENT RECOMMANDÉ : il
   * empêche un serveur malveillant de permuter deux entrées du même coffre
   * (le blob de l'entrée A ne s'ouvre plus sous l'identifiant B).
   *
   * Le même identifiant devra être fourni au déchiffrement.
   */
  readonly entryId?: string;
  readonly algorithm?: AeadAlgorithmId;
}

export interface DecryptEntryInput {
  readonly ciphertext: EncryptedEntry;
  readonly vaultKey: SecretKey;
  /** Doit correspondre exactement à l'`entryId` utilisé au chiffrement. */
  readonly entryId?: string;
}

function entryAad(entryId: string | undefined): Uint8Array | undefined {
  if (entryId === undefined) return undefined;
  if (typeof entryId !== 'string' || entryId.length === 0) {
    throw new InvalidParameterError('entryId: chaine non vide attendue');
  }
  return utf8ToBytes(entryId);
}

function serializeJson(value: unknown): string {
  const json = JSON.stringify(value);
  if (typeof json !== 'string') {
    throw new InvalidParameterError('entry: valeur non serialisable en JSON');
  }
  return json;
}

/**
 * Chiffre une valeur JSON quelconque sous la `vaultKey`.
 * Deux appels sur la même valeur produisent des ciphertexts DIFFÉRENTS
 * (nonce frais) : aucune corrélation possible côté serveur.
 */
export function encryptJson<T>(input: EncryptEntryInput<T>): EncryptedEntry {
  assertSecretKey(input?.vaultKey, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  const plaintext = utf8ToBytes(serializeJson(input.entry));
  try {
    return sealToString(input.vaultKey, plaintext, {
      context: SEAL_CONTEXT.vaultEntry,
      associatedData: entryAad(input.entryId),
      algorithm: input.algorithm,
    });
  } finally {
    wipe(plaintext);
  }
}

/**
 * Déchiffre une valeur JSON.
 *
 * @throws {DecryptionFailedError} mauvaise `vaultKey`, blob altéré, ou
 *   `entryId` non concordant
 */
export function decryptJson<T>(input: DecryptEntryInput): T {
  assertSecretKey(input?.vaultKey, 'vaultKey', SYMMETRIC_KEY_LENGTH);
  const plaintext = openFromString(input.vaultKey, input.ciphertext, {
    context: SEAL_CONTEXT.vaultEntry,
    associatedData: entryAad(input.entryId),
  });
  try {
    return JSON.parse(bytesToUtf8(plaintext)) as T;
  } catch (cause) {
    if (cause instanceof InvalidParameterError) throw cause;
    // Le contenu est authentifié : un JSON invalide signale une corruption
    // applicative, pas une attaque. Aucun extrait de clair n'est propagé.
    throw new InvalidParameterError('contenu dechiffre non conforme au JSON attendu');
  } finally {
    wipe(plaintext);
  }
}

/** Chiffre la partie secrète d'une entrée de coffre. */
export function encryptEntry(input: EncryptEntryInput<EntrySecrets>): EncryptedEntry {
  if (input?.entry === null || typeof input?.entry !== 'object') {
    throw new InvalidParameterError('entry: objet attendu');
  }
  return encryptJson(input);
}

/** Déchiffre la partie secrète d'une entrée de coffre. */
export function decryptEntry<T extends EntrySecrets = EntrySecrets>(
  input: DecryptEntryInput,
): T {
  const entry = decryptJson<T>(input);
  if (entry === null || typeof entry !== 'object' || Array.isArray(entry)) {
    throw new InvalidParameterError('entree dechiffree: objet attendu');
  }
  return entry;
}

export interface EncryptFieldInput {
  readonly value: string;
  readonly vaultKey: SecretKey;
  readonly entryId?: string;
  readonly algorithm?: AeadAlgorithmId;
}

/**
 * Chiffre un champ texte isolé (utile pour chiffrer un mot de passe seul, ou
 * pour un modèle « champ par champ » côté `vault-model`).
 */
export function encryptField(input: EncryptFieldInput): EncryptedEntry {
  if (typeof input?.value !== 'string') {
    throw new InvalidParameterError('value: chaine attendue');
  }
  return encryptJson({
    entry: input.value,
    vaultKey: input.vaultKey,
    entryId: input.entryId,
    algorithm: input.algorithm,
  });
}

/** Déchiffre un champ texte isolé. */
export function decryptField(input: DecryptEntryInput): string {
  const value = decryptJson<unknown>(input);
  if (typeof value !== 'string') {
    throw new InvalidParameterError('champ dechiffre: chaine attendue');
  }
  return value;
}
